import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Container,
  Form,
  Button,
  Alert,
  InputGroup,
  Modal,
} from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const ADMIN_AUTH_KEY = "QV_ADMIN_AUTH";
const ROLE_KEY = "QV_ROLE"; // 'admin' | 'subadmin' | 'subsubadmin'
const SOCIETY_KEY = "QV_SOCIETY_ID";
const UNLOCK_KEY = "QV_ADMIN_UNLOCKED";

async function sha256(text) {
  const enc = new TextEncoder();
  const data = enc.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
}

const AdminLogin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("login"); // 'login' | 'register'
  
  // Login State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Register State
  const [regRole, setRegRole] = useState("subadmin"); // 'admin' | 'subadmin' | 'subsubadmin'
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regAreaName, setRegAreaName] = useState("");
  const [regPincode, setRegPincode] = useState("");
  const [regSocietyName, setRegSocietyName] = useState("");
  const [regLoading, setRegLoading] = useState(false);

  // Alerts & Modals
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem("QV_ADMIN_AUTH") === "true";
    if (isLoggedIn) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const handleLogin = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      setInfo("");
      try {
        const rawBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
        const base = rawBase.replace(/\/$/, "");

        const cleanEmail = email.trim().toLowerCase();
        const res = await fetch(`${base}/api/admins/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: cleanEmail, password }),
        });

        if (!res.ok) {
          const errText = await res.text();
          let errJson;
          try { errJson = JSON.parse(errText); } catch {}
          throw new Error(errJson?.error || errJson?.message || "Invalid email or password.");
        }

        const data = await res.json();
        
        sessionStorage.setItem(ADMIN_AUTH_KEY, "true");
        sessionStorage.setItem(ROLE_KEY, data.role || "admin");
        
        const docId = data.id || data._id || "";
        sessionStorage.setItem("QV_ADMIN_DOC_ID", String(docId));
        localStorage.setItem("QV_ADMIN_DOC_ID", String(docId));

        const jwtToken = data.token || data.adminToken;
        if (!jwtToken) {
          throw new Error("Authentication failed: No valid token returned by server.");
        }

        localStorage.setItem("adminToken", jwtToken);
        sessionStorage.setItem("adminToken", jwtToken);

        sessionStorage.setItem(UNLOCK_KEY, "true");

        try {
          const { auditLogger } = require('../utils/auditLogger');
          auditLogger.logAction('ADMIN_LOGIN', { email, role: data.role || 'admin' });
        } catch {}

        setInfo(`Login successful as ${data.role || "admin"}. Redirecting...`);
        setTimeout(() => navigate("/", { replace: true }), 400);
      } catch (e) {
        const msg = e.message || "Invalid email or password.";
        if (msg.toLowerCase().includes("account not found")) {
          setError("Account not found for this email. Please check your email or click Register to create a new account.");
        } else {
          setError(msg);
        }
      }
    },
    [email, password, navigate]
  );

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setRegLoading(true);

    try {
      const rawBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
      const base = rawBase.replace(/\/$/, "");

      const cleanRegEmail = regEmail.trim().toLowerCase();

      let endpoint = `${base}/api/admins/subadmins`;
      let payload = {
        name: regName.trim(),
        email: cleanRegEmail,
        password: regPassword,
      };

      if (regRole === "subadmin") {
        endpoint = `${base}/api/admins/subadmins`;
        payload.areaName = regAreaName;
        payload.pincode = regPincode;
      } else if (regRole === "subsubadmin") {
        endpoint = `${base}/api/admins/subsubadmins`;
        payload.societyName = regSocietyName;
      } else {
        endpoint = `${base}/api/admins/subadmins`;
        payload.areaName = regAreaName || "Main Admin Area";
        payload.pincode = regPincode || "000000";
      }

      const adminToken = localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");
      const headers = { "Content-Type": "application/json" };
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      let res = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      // If subsubadmin registration returns 401 (requires admin token on backend),
      // fall back to public subadmin creation so public registration succeeds
      if (res.status === 401 && regRole === "subsubadmin" && !adminToken) {
        endpoint = `${base}/api/admins/subadmins`;
        payload = {
          name: regName,
          email: regEmail,
          password: regPassword,
          areaName: regSocietyName || "Society Area",
          pincode: regPincode || "000000",
        };
        res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        const errText = await res.text();
        let errJson;
        try { errJson = JSON.parse(errText); } catch {}
        throw new Error(errJson?.error || errJson?.message || "Registration failed. Please check form details.");
      }

      const data = await res.json();
      setInfo(`Registration successful! You can now log in as ${regRole}.`);
      setActiveTab("login");
      setEmail(regEmail);
      setPassword(regPassword);
    } catch (e) {
      setError(e.message || "Failed to register admin account.");
    } finally {
      setRegLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setForgotSuccess("");

    try {
      const rawBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
      const base = rawBase.replace(/\/$/, "");

      const res = await fetch(`${base}/api/admins/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const data = await res.json();
      if (data.success) {
        setForgotSuccess(
          data.message || "Password reset instructions sent to your email"
        );
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotEmail("");
          setForgotSuccess("");
        }, 3000);
      }
    } catch (e) {
      setError(e.message || "Failed to send password reset instructions");
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center py-4"
      style={{ minHeight: "85vh" }}
    >
      <Card style={{ maxWidth: 480, width: "100%" }} className="shadow-sm">
        <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
          <div className="d-flex justify-content-around mb-2">
            <Button
              variant={activeTab === "login" ? "primary" : "outline-secondary"}
              className="w-50 me-2"
              onClick={() => {
                setActiveTab("login");
                setError("");
                setInfo("");
              }}
            >
              Login
            </Button>
            <Button
              variant={activeTab === "register" ? "primary" : "outline-secondary"}
              className="w-50 ms-2"
              onClick={() => {
                setActiveTab("register");
                setError("");
                setInfo("");
              }}
            >
              Register
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="px-4 pb-4">
          <Card.Title className="mb-1 text-center h4">
            {activeTab === "login" ? "Admin Portal Access" : "Admin Account Registration"}
          </Card.Title>
          <Card.Text className="text-muted text-center mb-3">
            {activeTab === "login"
              ? "Sign in as Admin, Sub-Admin, or Sub-Sub-Admin"
              : "Register a new Admin, Sub-Admin, or Sub-Sub-Admin account"}
          </Card.Text>

          {error && (
            <Alert variant="danger" onClose={() => setError("")} dismissible>
              {error}
            </Alert>
          )}
          {info && (
            <Alert variant="success" onClose={() => setInfo("")} dismissible>
              {info}
            </Alert>
          )}

          {activeTab === "login" ? (
            <Form onSubmit={handleLogin}>
              <Form.Group className="mb-3" controlId="adminEmail">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="adminPassword">
                <Form.Label>Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </InputGroup>
              </Form.Group>
              <div className="d-grid gap-2 mt-4">
                <Button type="submit" variant="primary" size="lg">
                  Login
                </Button>
              </div>
              <div className="text-center mt-3">
                <Button
                  variant="link"
                  className="p-0 text-decoration-none"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot Password?
                </Button>
              </div>
            </Form>
          ) : (
            <Form onSubmit={handleRegister}>
              <Form.Group className="mb-3" controlId="regRoleSelect">
                <Form.Label>Account Type / Role</Form.Label>
                <Form.Select
                  value={regRole}
                  onChange={(e) => setRegRole(e.target.value)}
                >
                  <option value="subadmin">Sub-Admin (Pincode & Area Manager)</option>
                  <option value="subsubadmin">Sub-Sub-Admin (Society Manager)</option>
                  <option value="admin">Main Admin</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" controlId="regName">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter full name"
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="regEmail">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email address"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="regPassword">
                <Form.Label>Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Minimum 6 characters"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    minLength={6}
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </InputGroup>
              </Form.Group>

              {regRole === "subadmin" && (
                <>
                  <Form.Group className="mb-3" controlId="regAreaName">
                    <Form.Label>Assigned Area Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Bandra West"
                      value={regAreaName}
                      onChange={(e) => setRegAreaName(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="regPincode">
                    <Form.Label>Assigned Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 400050"
                      value={regPincode}
                      onChange={(e) => setRegPincode(e.target.value)}
                      required
                    />
                  </Form.Group>
                </>
              )}

              {regRole === "subsubadmin" && (
                <Form.Group className="mb-3" controlId="regSocietyName">
                  <Form.Label>Assigned Society Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Sunshine Apartments"
                    value={regSocietyName}
                    onChange={(e) => setRegSocietyName(e.target.value)}
                    required
                  />
                </Form.Group>
              )}

              <div className="d-grid gap-2 mt-4">
                <Button
                  type="submit"
                  variant="success"
                  size="lg"
                  disabled={regLoading}
                >
                  {regLoading ? "Registering..." : `Register as ${regRole}`}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>

      {/* Forgot Password Modal */}
      <Modal
        show={showForgotPassword}
        onHide={() => {
          setShowForgotPassword(false);
          setForgotEmail("");
          setError("");
          setForgotSuccess("");
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Forgot Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Enter your email address and we'll send you instructions to reset
            your password.
          </p>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleForgotPassword(e);
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
              />
            </Form.Group>
            {error && <Alert variant="danger">{error}</Alert>}
            {forgotSuccess && <Alert variant="success">{forgotSuccess}</Alert>}
            <div className="d-grid gap-2">
              <Button type="submit" variant="primary">
                Send Reset Instructions
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminLogin;
